import * as React from 'react';
// Import hooks.
import {
	useAddressVerification,
	UseAddressVerificationParameters,
	UseAddressVerificationResults,
} from '@app/hooks/view';
// Import enumerations.
import {
	GpsAccuracy
} from '@app/enumerations';
// Import props.
import {
	FormPopupCoordinatesSelectionProps,
} from '@app/props';
// Import api models.
import {
	Address,
	AddressLocation,
	GpsCoordinates,
	GpsCoordinatesWithAccuracy,
} from '@app/models/api';

interface UseAddressGeocodingParameters {
	addressVerificationParameters: Omit<UseAddressVerificationParameters, 'finishConfirmAddressVerification'>;
	geocodedAddressResult?: GpsCoordinatesWithAccuracy;
	geocodeAddress: (address: Address) => Promise<GpsCoordinatesWithAccuracy>
	finishConfirmAddressGeocoding: (address: AddressLocation, coordinates: GpsCoordinates) => void;
}

interface UseAddressGeocodingResults {
	addressVerification: UseAddressVerificationResults,
	applyFormPopupCoordinatesSelectionProps: () => FormPopupCoordinatesSelectionProps;
	isAddressCoordinateSelectionRequired: () => boolean;
}

const useAddressGeocoding = (params: UseAddressGeocodingParameters): UseAddressGeocodingResults => {

	// #region exported methods

	/**
	 * Checks to see if address coordinate selection is required. 
	 **/
	const isAddressCoordinateSelectionRequired = (): boolean => {
		return geocodedAddressResult !== undefined
			&& geocodedAddressResult.gpsAccuracy !== GpsAccuracy.ROOFTOP;
	}

	/**
	 * Returns props for single line address verification form. 
	 **/
	const applyFormPopupCoordinatesSelectionProps = (): FormPopupCoordinatesSelectionProps => {
		// Apply the single line address verification form props.
		return {
			isOpen: isAddressCoordinateSelectionRequired(),
			location: geocodedAddressResult,
			onSaveCoordinates: confirmGeocodedAddress,
			onClose: cancelAddressGeocoding,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Updates the geocoded address result.
	 * @param newGeocodedAddressResult
	 */
	const setGeocodedAddressResult = (newGeocodedAddressResult: GpsCoordinatesWithAccuracy | undefined): void => {
		_setGeocodedAddressResult(newGeocodedAddressResult);
	}

	/**
	 * Geocodes the address provided as the input.
	 * @param address
	 */
	const geocodeAddressAsync = async (address: Address): Promise<void> => {
		// Geocode address.
		let gpsCoordinatesWithAccuracy: GpsCoordinatesWithAccuracy = await params.geocodeAddress(address);

		if (gpsCoordinatesWithAccuracy.gpsAccuracy === GpsAccuracy.ROOFTOP) {
			// Save the results if roof top.
			params.finishConfirmAddressGeocoding({
				address: address,
				gpsCoordinates: null,
			}, gpsCoordinatesWithAccuracy);
		}
		else {
			// Set the new address result.
			addressVerification.addressLocationInput.setAddress(address);
			// Clear any address verification results.
			addressVerification.setVerifiedAddressResult(undefined);
			// Set the new geocoded address result.
			setGeocodedAddressResult(gpsCoordinatesWithAccuracy);
		}
	}

	/**
	 *  Confirm the geocoded coordinates as user specified.
	 **/
	const confirmGeocodedAddress = async (location: GpsCoordinates): Promise<void> => {
		const address: Address | undefined = addressVerification.addressLocationInput.value.address;

		if (address !== undefined) {
			// Clear any geocoded results.
			setGeocodedAddressResult(undefined);
			// Clear any address verification results.
			addressVerification.setVerifiedAddressResult(undefined);
			// Save address information.
			params.finishConfirmAddressGeocoding({
				address: address,
				gpsCoordinates: location,
			}, location);
		}
	}

	/**
	*  Resets the address verification results to undefined.
	**/
	const cancelAddressGeocoding = (): void => {
		setGeocodedAddressResult(undefined);
	}

	// #endregion private methods


	// #region variables

	// Set the initial geocoded address state.
	const [geocodedAddressResult, _setGeocodedAddressResult] = React.useState<GpsCoordinatesWithAccuracy | undefined>(undefined);

	// Instantiate the 'address verification' hook.
	const addressVerification: UseAddressVerificationResults = useAddressVerification({
		...params.addressVerificationParameters,
		finishConfirmAddressVerification: geocodeAddressAsync,
	});

	// #endregion variables


	return {
		addressVerification: addressVerification,
		applyFormPopupCoordinatesSelectionProps: applyFormPopupCoordinatesSelectionProps,
		isAddressCoordinateSelectionRequired: isAddressCoordinateSelectionRequired,
	};
}

// Export parameters.
export {
	UseAddressGeocodingParameters,
}

// Export results.
export {
	UseAddressGeocodingResults,
}

export default useAddressGeocoding;